import { AnyAction, applyMiddleware, compose, createStore, Store } from "redux";
import { persistReducer, persistStore } from "redux-persist";
import { PersistPartial } from "redux-persist/es/persistReducer";
import storage from "redux-persist/lib/storage"; // defaults to localStorage for web and AsyncStorage for react-native
import thunk from "redux-thunk";
import rootReducer from "./reducers";
import "./styles/global.scss";
import * as Sentry from "@sentry/react";
import Settings from "./helpers/Settings";
import AppStateInterface from "./interfaces/AppStateInterface";

const persistConfig = {
  key: "redux",
  storage,
  whitelist: ["flashMessage"],
};

const persistedReducer = persistReducer<AppStateInterface, AnyAction>(
  persistConfig,
  rootReducer,
);

// development
const createStoreForDev = () => {
  const composeWithDevtools = (window as any)
    .__REDUX_DEVTOOLS_EXTENSION_COMPOSE__;
  const composeEnhancers = composeWithDevtools
    ? composeWithDevtools({
        trace: true,
      })
    : compose;

  return createStore(
    persistedReducer,
    composeEnhancers(applyMiddleware(thunk)),
  );
};

// production
const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  stateTransformer: (state) => {
    // 個人情報が含まれるsessionはsentryに送信しない
    return { ...state, session: null };
  },
});
const createStoreForProd = () => {
  return createStore(
    persistedReducer,
    compose(applyMiddleware(thunk), sentryReduxEnhancer),
  );
};

export const createStoreForEnvironment = () => {
  if (Settings.ENVIRONMENT === "prod") {
    return createStoreForProd();
  } else {
    return createStoreForDev();
  }
};

const store: Store<AppStateInterface & PersistPartial, AnyAction> =
  createStoreForEnvironment();

// redux-persistのpersistorオブジェクト
// ref: https://github.com/rt2zz/redux-persist#persistor-object
// componentからpersistorオブジェクトのイベントを発火させたい場合があるので
// export して、外で使えるようにしてある
export const persistor = persistStore(store, {});

// redux-thunk が Dispatch interface をグローバルに宣言マージで拡張しているため、
// AppDispatch は明示的な ThunkDispatch 型でなくても thunk 関数を dispatch できる。
// この augmentation は src/index.tsx の `import type {} from "redux-thunk/extend-redux"`
// で有効化しているため、当該 import を消すと thunk の dispatch が型エラーになる。
export type AppDispatch = typeof store.dispatch;

// store
export default store;
